import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChevronDown, Mail, MessageSquareReply } from "lucide-react";
import {
  useReceivablesEmailStatsQuery,
  type EmailStatsPeriodGrouping,
  type ReceivablesEmailSenderStat,
  type ReceivablesEmailUnrespondingCustomer,
} from "../../api/queries/receivablesQueries";
import {
  EMAIL_SEND_PERIOD_GROUPING_OPTIONS,
  formatEmailSendPeriodLabel,
} from "../../utils/periodGrouping";
import {
  formatReplyRateHint,
  formatReplyRatePercent,
} from "../../utils/emailStatsFormat";

type ManagerFilters = {
  collectorId?: number;
  customerGroupId?: number;
};

type DashboardEmailStatsSectionProps = {
  startDate?: string;
  endDate?: string;
  managerFilters?: ManagerFilters;
  /** When true, omit the section header (parent zone provides it). */
  embedded?: boolean;
};

type SendVolumeChartPoint = {
  period: string;
  label: string;
  automated: number;
  manual: number;
  sent: number;
};

type ReplyVolumeChartPoint = {
  period: string;
  label: string;
  replies: number;
};

function EmailStatsKpiSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {[1, 2].map((key) => (
        <div
          key={key}
          className="h-44 animate-pulse rounded-xl border border-[#E5E7EB] bg-white"
        />
      ))}
    </div>
  );
}

function EmailStatsChartSkeleton() {
  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
      <div className="mb-3 h-9 animate-pulse rounded bg-[#F3F4F6]" />
      <div className="h-72 w-full animate-pulse rounded-lg bg-[#F3F4F6]" />
    </div>
  );
}

function EmailStatsErrorCard() {
  return (
    <div
      role="alert"
      className="rounded-xl border border-[#FECACA] bg-[#FEF2F2] px-4 py-3 text-sm text-[#DC2626]"
    >
      Unable to load email statistics. Existing receivables charts remain
      available.
    </div>
  );
}

function EmailStatsEmptyChart() {
  return (
    <div className="flex h-72 flex-col items-center justify-center rounded-lg border border-dashed border-[#E5E7EB] bg-[#FAFAFA] px-6 text-center">
      <p className="text-sm font-medium text-[#111827]">
        No outgoing emails sent
      </p>
      <p className="mt-1 text-sm text-[#6B7280]">
        No outgoing emails were sent in the selected period.
      </p>
    </div>
  );
}

type SendVolumeTooltipProps = {
  active?: boolean;
  payload?: ReadonlyArray<{
    value?: number;
    payload: SendVolumeChartPoint;
  }>;
  label?: string;
};

function SendVolumeTooltip({ active, payload }: SendVolumeTooltipProps) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="rounded-md border border-[#E5E7EB] bg-white/95 px-3 py-2 text-xs shadow-lg backdrop-blur-sm">
      <p className="mb-1 font-medium text-[#111827]">{point.label}</p>
      <div className="flex justify-between gap-4">
        <span className="text-[#6B7280]">Automated</span>
        <span className="tabular-nums font-medium text-[#111827]">
          {point.automated}
        </span>
      </div>
      <div className="mt-0.5 flex justify-between gap-4">
        <span className="text-[#6B7280]">Manual</span>
        <span className="tabular-nums font-medium text-[#111827]">
          {point.manual}
        </span>
      </div>
      <div className="mt-1 flex justify-between gap-4 border-t border-[#F3F4F6] pt-1">
        <span className="text-[#52514E]">Total</span>
        <span className="tabular-nums font-medium text-[#111827]">
          {point.sent}
        </span>
      </div>
    </div>
  );
}

type EmailStatsKpiRowProps = {
  senderStats: ReceivablesEmailSenderStat[];
  automatedSent: number;
  manualSent: number;
  totalReplies: number;
  unrespondingCustomers: ReceivablesEmailUnrespondingCustomer[];
};

function SendMixCard({
  automatedSent,
  manualSent,
}: {
  automatedSent: number;
  manualSent: number;
}) {
  const total = automatedSent + manualSent;
  return (
    <article className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <span className="rounded-lg bg-[#EEF4F2] p-2 text-[#4B7F73]">
          <Mail aria-hidden="true" className="h-5 w-5" />
        </span>
        <div>
          <h3 className="text-sm font-medium text-[#6B7280]">Emails sent</h3>
          <p className="text-sm text-[#9CA3AF]">Automated vs manual</p>
        </div>
      </div>
      <p className="mt-3 text-3xl font-semibold tabular-nums tracking-tight text-[#111827]">
        {total}
      </p>
      <dl className="mt-2 space-y-1 text-sm">
        <div className="flex justify-between gap-4">
          <dt className="text-[#6B7280]">Automated</dt>
          <dd className="tabular-nums font-medium text-[#111827]">
            {automatedSent}
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-[#6B7280]">Manual</dt>
          <dd className="tabular-nums font-medium text-[#111827]">
            {manualSent}
          </dd>
        </div>
      </dl>
    </article>
  );
}

function EmailsByUserCard({
  senderStats,
}: {
  senderStats: ReceivablesEmailSenderStat[];
}) {
  return (
    <article className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <span className="rounded-lg bg-[#EEF4F2] p-2 text-[#4B7F73]">
          <Mail aria-hidden="true" className="h-5 w-5" />
        </span>
        <div>
          <h3 className="text-sm font-medium text-[#6B7280]">Manual sends by user</h3>
          <p className="text-sm text-[#9CA3AF]">Manual template and compose only</p>
        </div>
      </div>
      <ul
        aria-label="Outgoing emails by user"
        className="mt-3 max-h-28 space-y-1 overflow-y-auto pr-1"
      >
        {senderStats.length === 0 && (
          <li className="px-2 py-1 text-sm text-[#9CA3AF]">No active users</li>
        )}
        {senderStats.map((sender) => (
          <li
            key={sender.userId}
            className="flex items-center justify-between gap-4 rounded-md px-2 py-1 text-sm"
          >
            <span className="truncate text-[#374151]">{sender.username}</span>
            <span className="tabular-nums font-medium text-[#111827]">
              {sender.emailCount}
            </span>
          </li>
        ))}
      </ul>
    </article>
  );
}

function TotalRepliesCard({ totalReplies }: { totalReplies: number }) {
  return (
    <article className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium text-[#6B7280]">
          Inbound replies
        </span>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#EEF4F2]">
          <MessageSquareReply
            className="h-4 w-4 text-[#4B7F73]"
            aria-hidden
          />
        </span>
      </div>
      <p className="mt-2 text-3xl font-semibold tabular-nums tracking-tight text-[#111827]">
        {totalReplies}
      </p>
      <p className="mt-1 text-sm text-[#6B7280]">
        Customer reply messages after first outreach
      </p>
    </article>
  );
}

function ReplyRateCard({
  customersEmailed,
  customersReplied,
  replyRate,
  unrespondingCustomers,
}: {
  customersEmailed: number;
  customersReplied: number;
  replyRate: number;
  unrespondingCustomers: ReceivablesEmailUnrespondingCustomer[];
}) {
  const unrespondingCount = unrespondingCustomers.length;

  return (
    <article className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium text-[#6B7280]">Reply rate</span>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#EEF4F2]">
          <MessageSquareReply
            className="h-4 w-4 text-[#4B7F73]"
            aria-hidden
          />
        </span>
      </div>
      <p className="mt-2 text-3xl font-semibold tabular-nums tracking-tight text-[#111827]">
        {formatReplyRatePercent(replyRate)}
      </p>
      <p className="mt-1 text-sm text-[#6B7280]">
        {formatReplyRateHint(customersReplied, customersEmailed)}
      </p>

      {unrespondingCount > 0 ? (
        <details className="group mt-3 border-t border-[#E5E7EB] pt-2">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-2 rounded-md py-1.5 text-sm font-medium text-[#374151] outline-none ring-[#4B7F73] hover:text-[#111827] focus-visible:ring-2 [&::-webkit-details-marker]:hidden">
            <span>
              View {unrespondingCount} unresponding customer
              {unrespondingCount === 1 ? "" : "s"}
            </span>
            <ChevronDown
              className="h-4 w-4 shrink-0 text-[#6B7280] transition-transform group-open:rotate-180"
              aria-hidden
            />
          </summary>
          <ul
            aria-label="Unresponding customers"
            className="mt-1 max-h-40 space-y-1 overflow-y-auto pr-1"
          >
            {unrespondingCustomers.map((customer) => (
              <li
                key={customer.customerId}
                className="truncate rounded-md px-2 py-1.5 text-sm text-[#374151]"
              >
                {customer.name}
              </li>
            ))}
          </ul>
        </details>
      ) : customersEmailed > 0 ? (
        <p className="mt-3 border-t border-[#E5E7EB] pt-2 text-sm text-[#6B7280]">
          All emailed customers replied
        </p>
      ) : null}
    </article>
  );
}

function EmailStatsKpiRow({
  senderStats,
  automatedSent,
  manualSent,
  totalReplies,
  unrespondingCustomers,
  customersEmailed,
  customersReplied,
  replyRate,
}: EmailStatsKpiRowProps & {
  customersEmailed: number;
  customersReplied: number;
  replyRate: number;
}) {
  return (
    <section
      aria-label="Email statistics KPIs"
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
    >
      <SendMixCard automatedSent={automatedSent} manualSent={manualSent} />
      <TotalRepliesCard totalReplies={totalReplies} />
      <EmailsByUserCard senderStats={senderStats} />
      {(customersEmailed > 0 || unrespondingCustomers.length > 0) && (
        <div className="sm:col-span-2 lg:col-span-3">
          <ReplyRateCard
            customersEmailed={customersEmailed}
            customersReplied={customersReplied}
            replyRate={replyRate}
            unrespondingCustomers={unrespondingCustomers}
          />
        </div>
      )}
    </section>
  );
}

type SendVolumeChartProps = {
  grouping: EmailStatsPeriodGrouping;
  onGroupingChange: (grouping: EmailStatsPeriodGrouping) => void;
  points: SendVolumeChartPoint[];
  isEmpty: boolean;
};

function SendVolumeChart({
  grouping,
  onGroupingChange,
  points,
  isEmpty,
}: SendVolumeChartProps) {
  return (
    <article
      aria-label="Send volume"
      className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm"
    >
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-base font-semibold text-[#111827]">Send volume</h3>
          <p className="text-sm text-[#6B7280]">
            Stacked by automated vs manual
          </p>
        </div>
        <div
          role="group"
          aria-label="Period grouping"
          className="inline-flex rounded-lg border border-[#E5E7EB] bg-[#F3F4F6] p-0.5"
        >
          {EMAIL_SEND_PERIOD_GROUPING_OPTIONS.map((option) => {
            const selected = option.value === grouping;
            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={selected}
                onClick={() => onGroupingChange(option.value)}
                className={
                  selected
                    ? "rounded-md bg-white px-3 py-1.5 text-sm font-medium text-[#111827] shadow-sm"
                    : "rounded-md px-3 py-1.5 text-sm font-medium text-[#6B7280] hover:text-[#111827]"
                }
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
      {isEmpty ? (
        <EmailStatsEmptyChart />
      ) : (
        <div
          className="h-72 w-full"
          role="img"
          aria-label="Send volume bar chart"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={points}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#6B7280" }} />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 12, fill: "#6B7280" }}
              />
              <Tooltip content={<SendVolumeTooltip />} />
              <Bar
                dataKey="automated"
                name="Automated"
                stackId="sends"
                fill="#4B7F73"
                radius={[0, 0, 0, 0]}
              />
              <Bar
                dataKey="manual"
                name="Manual"
                stackId="sends"
                fill="#93B5AD"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </article>
  );
}

export function DashboardEmailStatsSection({
  startDate,
  endDate,
  managerFilters,
  embedded = false,
}: DashboardEmailStatsSectionProps) {
  const [periodGrouping, setPeriodGrouping] =
    useState<EmailStatsPeriodGrouping>("week");

  const emailStatsQuery = useReceivablesEmailStatsQuery({
    startDate,
    endDate,
    periodGrouping,
    managerFilters,
  });

  const chartPoints = useMemo(() => {
    const volume = emailStatsQuery.data?.sendVolume ?? [];
    return volume.map((point) => ({
      period: point.period,
      label: formatEmailSendPeriodLabel(point.period, periodGrouping),
      automated: point.automatedSent,
      manual: point.manualSent,
      sent: point.sent,
    }));
  }, [emailStatsQuery.data?.sendVolume, periodGrouping]);

  const replyChartPoints = useMemo<ReplyVolumeChartPoint[]>(() => {
    const volume = emailStatsQuery.data?.replyVolume ?? [];
    return volume.map((point) => ({
      period: point.period,
      label: formatEmailSendPeriodLabel(point.period, periodGrouping),
      replies: point.replies,
    }));
  }, [emailStatsQuery.data?.replyVolume, periodGrouping]);

  const totalSent = chartPoints.reduce((sum, point) => sum + point.sent, 0);
  const isEmpty =
    (emailStatsQuery.data?.customersEmailed ?? 0) === 0 && totalSent === 0;

  const sectionHeader = embedded ? null : (
    <header>
      <h2 className="text-lg font-semibold text-[#111827]">Email statistics</h2>
      <p className="mt-1 text-sm text-[#6B7280]">Outgoing email performance</p>
    </header>
  );

  if (emailStatsQuery.isError) {
    return (
      <section aria-label="Email statistics" className="space-y-4">
        {sectionHeader}
        <EmailStatsErrorCard />
      </section>
    );
  }

  if (emailStatsQuery.isPending) {
    return (
      <section aria-label="Email statistics" className="space-y-4">
        {sectionHeader}
        <EmailStatsKpiSkeleton />
        <EmailStatsChartSkeleton />
      </section>
    );
  }

  const automatedSent = emailStatsQuery.data?.automatedSent ?? 0;
  const manualSent = emailStatsQuery.data?.manualSent ?? 0;
  const totalReplies = emailStatsQuery.data?.totalReplies ?? 0;
  const customersEmailed = emailStatsQuery.data?.customersEmailed ?? 0;
  const customersReplied = emailStatsQuery.data?.customersReplied ?? 0;
  const replyRate = emailStatsQuery.data?.replyRate ?? 0;
  const senderStats = emailStatsQuery.data?.senderStats ?? [];
  const unrespondingCustomers =
    emailStatsQuery.data?.unrespondingCustomers ?? [];

  return (
    <section aria-label="Email statistics" className="space-y-4">
      {sectionHeader}
      <EmailStatsKpiRow
        senderStats={senderStats}
        automatedSent={automatedSent}
        manualSent={manualSent}
        totalReplies={totalReplies}
        unrespondingCustomers={unrespondingCustomers}
        customersEmailed={customersEmailed}
        customersReplied={customersReplied}
        replyRate={replyRate}
      />
      <SendVolumeChart
        grouping={periodGrouping}
        onGroupingChange={setPeriodGrouping}
        points={chartPoints}
        isEmpty={isEmpty}
      />
      {replyChartPoints.length > 0 ? (
        <article
          aria-label="Reply volume"
          className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm"
        >
          <h3 className="text-base font-semibold text-[#111827]">
            Reply volume
          </h3>
          <p className="mt-0.5 text-sm text-[#6B7280]">
            Inbound customer messages by period
          </p>
          <div
            className="mt-3 h-56 w-full"
            role="img"
            aria-label="Reply volume bar chart"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={replyChartPoints}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12, fill: "#6B7280" }}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#6B7280" }} />
                <Tooltip />
                <Bar
                  dataKey="replies"
                  name="Replies"
                  fill="#64748B"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </article>
      ) : null}
    </section>
  );
}
